/**
 * ChatDebugPanel - Pannello debug completo per WhatsApp
 * Visibile SOLO in development (import.meta.env.DEV)
 * Toggle globale: accessibile da sidebar e da chat header
 *
 * Due tab:
 *  - LOGS: flusso eventi in tempo reale
 *  - STATE: stato live di store, componenti, cache
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { X, Trash2, Copy, ChevronDown, ChevronRight } from 'lucide-react';
import { useWhatsAppChatStore } from '@/lib/stores/whatsappChatStore';

// ============ DEBUG LOG SYSTEM ============

export type DebugLogLevel = 'info' | 'warn' | 'error' | 'data';

export interface DebugLogEntry {
  id: number;
  timestamp: Date;
  level: DebugLogLevel;
  source: string;
  message: string;
  data?: unknown;
}

let logIdCounter = 0;
const logListeners = new Set<(entry: DebugLogEntry) => void>();
const logHistory: DebugLogEntry[] = [];
const MAX_LOG_HISTORY = 500;

export function debugLog(source: string, message: string, data?: unknown, level: DebugLogLevel = 'info') {
  if (!import.meta.env.DEV) return;
  const entry: DebugLogEntry = {
    id: logIdCounter++,
    timestamp: new Date(),
    level,
    source,
    message,
    data,
  };
  logHistory.push(entry);
  if (logHistory.length > MAX_LOG_HISTORY) logHistory.splice(0, logHistory.length - MAX_LOG_HISTORY);
  for (const listener of logListeners) listener(entry);
}

export function useDebugLogs() {
  const [logs, setLogs] = useState<DebugLogEntry[]>(() => [...logHistory]);

  useEffect(() => {
    const handler = (entry: DebugLogEntry) => {
      setLogs(prev => [...prev, entry]);
    };
    logListeners.add(handler);
    return () => { logListeners.delete(handler); };
  }, []);

  const clear = useCallback(() => {
    logHistory.length = 0;
    setLogs([]);
  }, []);

  return { logs, clear };
}

// ============ GLOBAL TOGGLE STATE ============
// Permette a qualsiasi componente di aprire/chiudere il pannello senza prop drilling

let _debugPanelOpen = false;
const _panelListeners = new Set<(open: boolean) => void>();

export function toggleDebugPanel() {
  if (!import.meta.env.DEV) return;
  _debugPanelOpen = !_debugPanelOpen;
  for (const l of _panelListeners) l(_debugPanelOpen);
}

export function useDebugPanelOpen() {
  const [open, setOpen] = useState(_debugPanelOpen);
  useEffect(() => {
    _panelListeners.add(setOpen);
    return () => { _panelListeners.delete(setOpen); };
  }, []);
  return open;
}

// ============ UI COMPONENTS ============

const LEVEL_COLORS: Record<DebugLogLevel, string> = {
  info: 'text-blue-400',
  warn: 'text-amber-400',
  error: 'text-red-400',
  data: 'text-emerald-400',
};

const LEVEL_BG: Record<DebugLogLevel, string> = {
  info: 'bg-blue-500/10',
  warn: 'bg-amber-500/10',
  error: 'bg-red-500/10',
  data: 'bg-emerald-500/10',
};

function LogEntry({ entry }: { entry: DebugLogEntry }) {
  const [expanded, setExpanded] = useState(false);
  const hasData = entry.data !== undefined;
  const time = entry.timestamp.toLocaleTimeString('it-IT', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit', fractionalSecondDigits: 3 });

  return (
    <div className={`px-2 py-1 border-b border-gray-800 hover:bg-gray-800/50 text-[11px] font-mono ${LEVEL_BG[entry.level]}`}>
      <div className="flex items-start gap-1.5">
        {hasData ? (
          <button onClick={() => setExpanded(!expanded)} className="flex-shrink-0 mt-0.5 text-gray-500 hover:text-gray-300">
            {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          </button>
        ) : (
          <span className="w-3 flex-shrink-0" />
        )}
        <span className="text-gray-500 flex-shrink-0">{time}</span>
        <span className={`flex-shrink-0 font-semibold ${LEVEL_COLORS[entry.level]}`}>[{entry.source}]</span>
        <span className="text-gray-300 break-all">{entry.message}</span>
      </div>
      {expanded && hasData && (
        <pre className="mt-1 ml-6 p-2 bg-gray-900 rounded text-[10px] text-gray-400 overflow-x-auto max-h-60 whitespace-pre-wrap">
          {typeof entry.data === 'string' ? entry.data : JSON.stringify(entry.data, null, 2)}
        </pre>
      )}
    </div>
  );
}

// ============ STATE TAB ============

function StateValue({ label, value, color }: { label: string; value: string | number | boolean | undefined; color?: string }) {
  const display = value === undefined ? 'undefined' : value === null ? 'null' : String(value);
  return (
    <div className="flex items-center justify-between py-0.5">
      <span className="text-gray-500">{label}</span>
      <span className={color || (typeof value === 'number' ? 'text-cyan-400' : typeof value === 'boolean' ? (value ? 'text-emerald-400' : 'text-red-400') : 'text-gray-300')}>{display}</span>
    </div>
  );
}

function StateSection({ title, children, defaultOpen = true }: { title: string; children: React.ReactNode; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-gray-800">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-1 px-2 py-1.5 text-[11px] font-mono font-bold text-amber-400 hover:bg-gray-800/50">
        {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        {title}
      </button>
      {open && <div className="px-3 pb-2 text-[10px] font-mono">{children}</div>}
    </div>
  );
}

function StateTab() {
  // Forza re-render ogni 2s per stato live
  const [, setTick] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => setTick(t => t + 1), 2000);
    return () => clearInterval(interval);
  }, []);

  const store = useWhatsAppChatStore.getState();
  const chats = store.chats;
  const messages = store.messages;
  const presenceMap = store.presenceMap;

  // Calcoli chat
  const groupChats = chats.filter(c => c.isGroup);
  const communityChats = chats.filter(c => c.id.startsWith('120363'));
  const withUnread = chats.filter(c => (c.unreadCount || 0) > 0);
  const totalUnread = withUnread.reduce((sum, c) => sum + (c.unreadCount || 0), 0);
  const withProfilePic = chats.filter(c => c.profilePicUrl);
  const onlineChats = Array.from(presenceMap.entries()).filter(([, p]) => p.status === 'online');
  const typingChats = Array.from(presenceMap.entries()).filter(([, p]) => p.status === 'typing' || p.status === 'recording');

  // Calcoli messaggi
  const inbound = messages.filter(m => m.direction === 'inbound');
  const outbound = messages.filter(m => m.direction === 'outbound');
  const withSenderJid = inbound.filter(m => m.senderJid);
  const withSenderName = inbound.filter(m => m.senderName);
  const mediaMessages = messages.filter(m => m.mediaUrl);

  // Chat corrente
  const selectedChat = chats.find(c => c.id === store.selectedChatId);

  return (
    <div className="flex-1 overflow-y-auto">
      {/* Store globale */}
      <StateSection title="STORE">
        <StateValue label="chats" value={chats.length} />
        <StateValue label="gruppi" value={groupChats.length} />
        <StateValue label="community (120363...)" value={communityChats.length} />
        <StateValue label="con unread" value={withUnread.length} />
        <StateValue label="totale unread" value={totalUnread} />
        <StateValue label="con profilePic" value={withProfilePic.length} />
        <StateValue label="chatsLoading" value={store.chatsLoading} />
        <StateValue label="chatsError" value={store.chatsError || 'null'} color={store.chatsError ? 'text-red-400' : undefined} />
        <StateValue label="chatsHasMore" value={store.chatsHasMore} />
        <StateValue label="chatsOffset" value={store.chatsOffset} />
      </StateSection>

      {/* Presence */}
      <StateSection title="PRESENCE" defaultOpen={false}>
        <StateValue label="tracked" value={presenceMap.size} />
        <StateValue label="online" value={onlineChats.length} />
        <StateValue label="typing/recording" value={typingChats.length} />
        {onlineChats.slice(0, 8).map(([id, p]) => (
          <div key={id} className="flex items-center gap-1 py-0.5">
            <span className="w-2 h-2 rounded-full bg-emerald-400 flex-shrink-0" />
            <span className="text-gray-400 truncate">{chats.find(c => c.id === id)?.name || id.slice(0, 20)}</span>
          </div>
        ))}
        {typingChats.map(([id, p]) => (
          <div key={id} className="flex items-center gap-1 py-0.5">
            <span className="w-2 h-2 rounded-full bg-blue-400 animate-pulse flex-shrink-0" />
            <span className="text-blue-400 truncate">{chats.find(c => c.id === id)?.name || id.slice(0, 20)} ({p.status})</span>
          </div>
        ))}
      </StateSection>

      {/* Chat selezionata */}
      <StateSection title="CHAT SELEZIONATA">
        {selectedChat ? (
          <>
            <StateValue label="id" value={selectedChat.id.slice(0, 30)} />
            <StateValue label="name" value={selectedChat.name} />
            <StateValue label="isGroup" value={selectedChat.isGroup} />
            <StateValue label="ends @g.us" value={selectedChat.id.endsWith('@g.us')} />
            <StateValue label="starts 120363" value={selectedChat.id.startsWith('120363')} />
            <StateValue label="unreadCount" value={selectedChat.unreadCount || 0} />
            <StateValue label="profilePicUrl" value={selectedChat.profilePicUrl ? 'YES' : 'no'} color={selectedChat.profilePicUrl ? 'text-emerald-400' : 'text-gray-600'} />
            <StateValue label="participantsCount" value={selectedChat.participantsCount || 0} />
            <div className="mt-1 border-t border-gray-800 pt-1">
              <StateValue label="messaggi totali" value={messages.length} />
              <StateValue label="inbound" value={inbound.length} />
              <StateValue label="outbound" value={outbound.length} />
              <StateValue label="con senderJid" value={`${withSenderJid.length}/${inbound.length}`} color={withSenderJid.length === inbound.length ? 'text-emerald-400' : 'text-amber-400'} />
              <StateValue label="con senderName" value={`${withSenderName.length}/${inbound.length}`} color={withSenderName.length === inbound.length ? 'text-emerald-400' : 'text-amber-400'} />
              <StateValue label="con media" value={mediaMessages.length} />
              <StateValue label="messagesLoading" value={store.messagesLoading} />
              <StateValue label="hasOlderMessages" value={store.hasOlderMessages} />
            </div>
          </>
        ) : (
          <span className="text-gray-600">Nessuna chat selezionata</span>
        )}
      </StateSection>

      {/* Top 15 chat con dettagli */}
      <StateSection title="TOP 15 CHAT" defaultOpen={false}>
        {chats.slice(0, 15).map((c, i) => (
          <div key={c.id} className={`py-1 ${i > 0 ? 'border-t border-gray-800/50' : ''}`}>
            <div className="flex items-center gap-1">
              <span className="text-gray-600 w-4 text-right">{i}</span>
              <span className={`${c.isGroup ? 'text-purple-400' : 'text-gray-300'} truncate flex-1`}>{c.name}</span>
              {(c.unreadCount || 0) > 0 && <span className="text-emerald-400 text-[9px] bg-emerald-400/10 px-1 rounded">{c.unreadCount}</span>}
            </div>
            <div className="ml-5 text-gray-600">
              {c.id.slice(0, 28)} | grp:{String(!!c.isGroup)} | pic:{c.profilePicUrl ? 'Y' : 'N'}
            </div>
          </div>
        ))}
      </StateSection>

      {/* Config WAHA */}
      <StateSection title="WAHA CONFIG" defaultOpen={false}>
        {store.wahaConfig ? (
          <>
            <StateValue label="configId" value={store.wahaConfig.configId?.slice(0, 20)} />
            <StateValue label="session" value={store.wahaConfig.session} />
            <StateValue label="organizationId" value={store.wahaConfig.organizationId?.slice(0, 20)} />
            <StateValue label="apiUrl" value={store.wahaConfig.apiUrl?.slice(0, 35)} />
          </>
        ) : (
          <span className="text-red-400">NON CONFIGURATO</span>
        )}
      </StateSection>
    </div>
  );
}

// ============ MAIN PANEL ============

export function ChatDebugPanel() {
  const isOpen = useDebugPanelOpen();
  const { logs, clear } = useDebugLogs();
  const [filter, setFilter] = useState<string>('');
  const [autoScroll, setAutoScroll] = useState(true);
  const [activeTab, setActiveTab] = useState<'logs' | 'state'>('logs');
  const scrollRef = useRef<HTMLDivElement>(null);
  const messages = useWhatsAppChatStore(s => s.messages);
  const selectedChatId = useWhatsAppChatStore(s => s.selectedChatId);
  const chats = useWhatsAppChatStore(s => s.chats);

  // Auto-scroll
  useEffect(() => {
    if (autoScroll && scrollRef.current && activeTab === 'logs') {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [logs.length, autoScroll, activeTab]);

  const filteredLogs = useMemo(() => {
    if (!filter) return logs;
    const f = filter.toLowerCase();
    return logs.filter(l => l.source.toLowerCase().includes(f) || l.message.toLowerCase().includes(f));
  }, [logs, filter]);

  // COPY ALL: snapshot completo di tutto (logs + state + chat list + messaggi) → clipboard
  const handleCopyAll = useCallback(() => {
    const store = useWhatsAppChatStore.getState();
    const allChats = store.chats;
    const allMessages = store.messages;
    const presMap = store.presenceMap;
    const selChatId = store.selectedChatId;
    const selChat = allChats.find(c => c.id === selChatId);

    const groupChats = allChats.filter(c => c.isGroup);
    const communityChats = allChats.filter(c => c.id.startsWith('120363'));
    const withUnread = allChats.filter(c => (c.unreadCount || 0) > 0);
    const inboundMsgs = allMessages.filter(m => m.direction === 'inbound');
    const onlineEntries = Array.from(presMap.entries()).filter(([, p]) => p.status === 'online');
    const typingEntries = Array.from(presMap.entries()).filter(([, p]) => p.status === 'typing' || p.status === 'recording');

    const sections: string[] = [];

    // --- LOGS ---
    sections.push('=== LOGS ===');
    sections.push(filteredLogs.map(l =>
      `[${l.timestamp.toISOString()}] [${l.level}] [${l.source}] ${l.message}${l.data ? '\n  ' + JSON.stringify(l.data) : ''}`
    ).join('\n'));

    // --- STORE STATE ---
    sections.push('\n=== STORE STATE ===');
    sections.push(JSON.stringify({
      chats: allChats.length,
      gruppi: groupChats.length,
      community: communityChats.length,
      conUnread: withUnread.length,
      totaleUnread: withUnread.reduce((s, c) => s + (c.unreadCount || 0), 0),
      chatsLoading: store.chatsLoading,
      chatsError: store.chatsError || null,
      chatsHasMore: store.chatsHasMore,
      chatsOffset: store.chatsOffset,
      presence: { tracked: presMap.size, online: onlineEntries.length, typing: typingEntries.length },
      wahaConfig: store.wahaConfig ? {
        configId: store.wahaConfig.configId,
        session: store.wahaConfig.session,
        organizationId: store.wahaConfig.organizationId,
        apiUrl: store.wahaConfig.apiUrl,
      } : null,
    }, null, 2));

    // --- CHAT SELEZIONATA ---
    sections.push('\n=== CHAT SELEZIONATA ===');
    if (selChat) {
      const withSenderJid = inboundMsgs.filter(m => m.senderJid);
      const withSenderName = inboundMsgs.filter(m => m.senderName);
      sections.push(JSON.stringify({
        id: selChat.id,
        name: selChat.name,
        isGroup: selChat.isGroup,
        endsGus: selChat.id.endsWith('@g.us'),
        starts120363: selChat.id.startsWith('120363'),
        unreadCount: selChat.unreadCount || 0,
        profilePicUrl: selChat.profilePicUrl || null,
        participantsCount: selChat.participantsCount || 0,
        messaggi: allMessages.length,
        inbound: inboundMsgs.length,
        outbound: allMessages.length - inboundMsgs.length,
        conSenderJid: `${withSenderJid.length}/${inboundMsgs.length}`,
        conSenderName: `${withSenderName.length}/${inboundMsgs.length}`,
        conMedia: allMessages.filter(m => m.mediaUrl).length,
        messagesLoading: store.messagesLoading,
        hasOlderMessages: store.hasOlderMessages,
      }, null, 2));

      // Sample messaggi inbound
      sections.push('\n--- Messaggi inbound (sample 20) ---');
      sections.push(JSON.stringify(inboundMsgs.slice(0, 20).map(m => ({
        id: m.id?.slice(0, 20),
        senderJid: m.senderJid || 'MANCANTE',
        senderName: m.senderName || 'MANCANTE',
        content: (m.content || '').slice(0, 60),
        type: m.type,
        direction: m.direction,
      })), null, 2));
    } else {
      sections.push('Nessuna chat selezionata');
    }

    // --- ELENCO CHAT (top 30) ---
    sections.push('\n=== ELENCO CHAT (top 30) ===');
    sections.push(JSON.stringify(allChats.slice(0, 30).map((c, i) => ({
      '#': i,
      id: c.id,
      name: c.name,
      isGroup: c.isGroup,
      unread: c.unreadCount || 0,
      hasPic: !!c.profilePicUrl,
      pinned: c.pinned,
      lastMsg: c.lastMessage?.body?.slice(0, 50) || '-',
      lastTs: c.lastMessage?.timestamp ? new Date(c.lastMessage.timestamp * 1000).toISOString() : '-',
      lastFromMe: c.lastMessage?.fromMe,
    })), null, 2));

    // --- UNREAD ---
    if (withUnread.length > 0) {
      sections.push('\n=== CHAT CON UNREAD ===');
      sections.push(JSON.stringify(withUnread.map(c => ({
        id: c.id,
        name: c.name,
        unread: c.unreadCount,
      })), null, 2));
    }

    // --- PRESENCE ---
    if (onlineEntries.length > 0 || typingEntries.length > 0) {
      sections.push('\n=== PRESENCE ===');
      sections.push(JSON.stringify({
        online: onlineEntries.map(([id]) => allChats.find(c => c.id === id)?.name || id),
        typing: typingEntries.map(([id, p]) => `${allChats.find(c => c.id === id)?.name || id} (${p.status})`),
      }, null, 2));
    }

    const fullText = sections.join('\n');
    navigator.clipboard.writeText(fullText);
    debugLog('Debug', `Copiato snapshot completo (${fullText.length} chars)`, null, 'info');
  }, [filteredLogs]);

  if (!isOpen) return null;

  return (
    <div className="w-[440px] flex-shrink-0 flex flex-col bg-[#1a1a2e] border-l border-gray-700 h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-700 bg-[#16162a]">
        <div className="flex items-center gap-1">
          {/* Tabs */}
          <button
            onClick={() => setActiveTab('logs')}
            className={`px-2 py-0.5 text-[10px] font-mono font-bold rounded ${activeTab === 'logs' ? 'bg-emerald-400/20 text-emerald-400' : 'text-gray-500 hover:text-gray-300'}`}
          >
            LOGS
          </button>
          <button
            onClick={() => setActiveTab('state')}
            className={`px-2 py-0.5 text-[10px] font-mono font-bold rounded ${activeTab === 'state' ? 'bg-amber-400/20 text-amber-400' : 'text-gray-500 hover:text-gray-300'}`}
          >
            STATE
          </button>
          <span className="text-[10px] text-gray-600 font-mono ml-1">{activeTab === 'logs' ? `${filteredLogs.length}` : ''}</span>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={handleCopyAll} className="flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono font-bold text-cyan-400 hover:bg-cyan-400/10 rounded" title="Snapshot completo → clipboard (logs + state + chat list + messaggi)">
            <Copy className="w-3 h-3" />
            COPY ALL
          </button>
          <button onClick={clear} className="p-1 text-gray-500 hover:text-red-400 rounded" title="Pulisci log">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
          <button onClick={toggleDebugPanel} className="p-1 text-gray-500 hover:text-gray-300 rounded">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Tab content */}
      {activeTab === 'logs' ? (
        <>
          {/* Filter */}
          <div className="px-2 py-1.5 border-b border-gray-700 flex gap-2 items-center">
            <input
              value={filter}
              onChange={e => setFilter(e.target.value)}
              placeholder="Filtra per source o testo..."
              className="flex-1 bg-gray-900 border border-gray-700 rounded px-2 py-1 text-[11px] font-mono text-gray-300 placeholder:text-gray-600 focus:outline-none focus:border-emerald-500"
            />
            <label className="flex items-center gap-1 text-[10px] text-gray-500 cursor-pointer">
              <input type="checkbox" checked={autoScroll} onChange={e => setAutoScroll(e.target.checked)} className="w-3 h-3" />
              Auto
            </label>
          </div>

          {/* Logs */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto overflow-x-hidden">
            {filteredLogs.length === 0 ? (
              <div className="flex items-center justify-center h-32 text-gray-600 text-xs font-mono">
                Nessun log. Interagisci con WhatsApp per vedere il flusso.
              </div>
            ) : (
              filteredLogs.map(entry => <LogEntry key={entry.id} entry={entry} />)
            )}
          </div>
        </>
      ) : (
        <StateTab />
      )}
    </div>
  );
}
